import React, { useState, useEffect, useRef } from 'react';

const apiUrlMultivac = process.env.REACT_APP_MULTIVAC_URL;

type FilterOption = {
  label: string;
  property: string;
  values: string[];
};

type Filter = {
  property: string;
  values: string;  // This is a single string in the internal implementation
};

type FilterOutput = {
  property: string;
  values: string[];  // This is the format expected by the parent component
};

type ColumnConfig = {
  field: string;
  header: string;
  width: string;
};

type SectionConfig = {
  title: string;
  fields: string[];
  customRenders?: (() => React.ReactNode | null)[];
  afterCustomRenderFields?: string[];
};

interface FilteredTableProps {
  filterOptions: Array<{
    label: string;
    property: string;
    values: string[];
  }>;
  onFiltersChange: (filters: FilterOutput[]) => void;
  initialFilters?: FilterOutput[];
  columnConfigs?: ColumnConfig[];
  preselectedPersona?: any;
  showFirstResultInModal?: boolean;
}

const FilteredTable: React.FC<FilteredTableProps> = ({ 
  filterOptions, 
  onFiltersChange,
  initialFilters = [],
  columnConfigs,
  preselectedPersona = null,
  showFirstResultInModal = false
}) => {
  const [selectedRow, setSelectedRow] = useState<any | null>(preselectedPersona);
  const [showModal, setShowModal] = useState(!!preselectedPersona || showFirstResultInModal);
  const [activeFilters, setActiveFilters] = useState<Filter[]>([]);
  const [pendingFilters, setPendingFilters] = useState<Filter[]>([]);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dropdownStates, setDropdownStates] = useState<Record<string, boolean>>({});
  const [applyingFilters, setApplyingFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<{ property: string; values: string[]; }[]>([]);
  // Add a ref to track if initial data fetch has been made
  const initialFetchMade = useRef(false);
  const isInitializing = useRef(true);

  // Define the fixed columns we want to show in the table
  const tableColumnConfigs: ColumnConfig[] = [
    { field: 'industry', header: 'Industry', width: 'min-w-[200px]' },
    { field: 'employee_range', header: 'Company Size', width: 'min-w-[200px]' },
    { field: 'role_set_name', header: 'Role Set', width: 'min-w-[200px]' }
  ];

  // Default column configurations
  const defaultColumnConfigs: ColumnConfig[] = [
    {field: 'good_personalized_copy', header: 'Good Copy', width: 'min-w-[200px]'},
    {field: 'bad_personalized_copy', header: 'Bad Copy', width: 'min-w-[200px]'},
    { field: 'industry', header: 'Industry', width: 'min-w-[200px]' },
    { field: 'employee_range', header: 'Company Size', width: 'min-w-[200px]' },
    //{ field: 'type_of_company', header: 'Type of Company', width: 'min-w-[200px]' },
    { field: 'role_set_name', header: 'Role Set', width: 'min-w-[200px]' },
    { field: 'roles', header: 'Roles', width: 'min-w-[200px]' },
    { field: 'gender_distribution', header: 'Gender Distribution', width: 'min-w-[200px]' },
    { field: 'age_distribution', header: 'Age Distribution', width: 'min-w-[200px]' },
    { field: 'yoe_distribution', header: 'Years of Experience', width: 'min-w-[200px]' },
    { field: 'what_are_they', header: 'What Are They', width: 'min-w-[400px]' },
    { field: 'how_they_contribute', header: 'How They Contribute', width: 'min-w-[400px]' },
    { field: 'high_icp_what_are_they', header: 'High ICP - What Are They', width: 'min-w-[400px]' },
    { field: 'high_icp_how_they_contribute', header: 'High ICP - How They Contribute', width: 'min-w-[400px]' },
    { field: 'phrases_topics', header: 'Phrases & Topics', width: 'min-w-[400px]' },
    { field: 'metrics', header: 'Metrics', width: 'min-w-[400px]' },
    { field: 'team', header: 'Team', width: 'min-w-[400px]' },
    { field: 'reports_to', header: 'Reports To', width: 'min-w-[400px]' },
    { field: 'salary_range', header: 'Salary Range', width: 'min-w-[200px]' },
    {field: 'day_in_the_life_data', header: 'Daily Schedule', width: 'min-w-[400px]'}
  ];

  // Keep the full column config for the modal view
  const fullColumnConfigs = columnConfigs || defaultColumnConfigs;


  const finalColumnConfigs = columnConfigs || defaultColumnConfigs;

  const formatCellContent = (content: any, field: string): React.ReactNode => {
    const arrayFields = ['roles', 'reports_to', 'team', 'phrases_topics', 'metrics'];
    if (Array.isArray(content) && arrayFields.includes(field)) {
      return (
        <div className="flex flex-wrap gap-2">
          {content.map((item, index) => (
            <span
              key={index}
              className="bg-pink-100 text-pink-800 px-2 py-1 rounded-full text-sm"
            >
              {item}
            </span>
          ))}
        </div>
      );
    }
    return String(content || '');
  };

  // Modified useEffect to handle both dropdown and modal clicks
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Handle modal clicks
      if (showModal && !target.closest('.modal-content')) {
        setShowModal(false);
      }
      
      // Handle dropdown clicks (only if modal is not showing)
      if (!showModal && !target.closest('.filter-dropdown')) {
        setDropdownStates({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showModal]);

  const toggleDropdown = (property: string) => {
    setDropdownStates(prev => {
      // Close all dropdowns and only toggle the clicked one
      const newState = { [property]: !prev[property] };
      return newState;
    });
  };

  const handleFilterChange = (property: string, value: string) => {
    setFilterValues(prev => ({
      ...prev,
      [property]: value
    }));

    if (value) {
      const newFilter = {
        property,
        values: value
      };
      
      setPendingFilters(prev => [
        ...prev.filter(f => f.property !== property),
        newFilter
      ]);
    } else {
      setPendingFilters(prev => prev.filter(f => f.property !== property));
    }

    setDropdownStates(prev => ({
      ...prev,
      [property]: false
    }));
  };

  const clearAllFilters = () => {
    // Skip if there are no filters to clear
    if (pendingFilters.length === 0) return;
    
    setPendingFilters([]);
    setFilterValues({});
  };

  const applyFilters = () => {
    // Check if filters have actually changed
    const filtersEqual = 
      activeFilters.length === pendingFilters.length && 
      activeFilters.every(active => 
        pendingFilters.some(pending => 
          pending.property === active.property && 
          pending.values === active.values
        )
      );
    
    // Skip if filters haven't changed
    if (filtersEqual) return;
    
    setActiveFilters(pendingFilters);
    setApplyingFilters(true);
    
    // Update URL with filters
    const params = new URLSearchParams();
    pendingFilters.forEach(filter => {
      params.set(filter.property, filter.values);
    });
    window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  // Load filters from URL on mount
  useEffect(() => {
    // This first useEffect handles ALL initialization logic
    const params = new URLSearchParams(window.location.search);
    const urlFilters: Filter[] = [];
    const newFilterValues: Record<string, string> = {};
    const hasUrlParams = params.toString() !== '';

    // First check URL parameters (highest priority)
    if (hasUrlParams) {
      params.forEach((value, property) => {
        urlFilters.push({ property, values: value });
        newFilterValues[property] = value;
      });

      setPendingFilters(urlFilters);
      setActiveFilters(urlFilters);
      setFilterValues(newFilterValues);
      
      // Immediately fetch data with URL filters
      console.log('Fetching data with URL filters:', urlFilters);
      fetchData(urlFilters);
      initialFetchMade.current = true;
      
      // Also notify parent component about the filters - only once
      const formattedFilters = urlFilters.map(filter => ({
        property: filter.property,
        values: [filter.values] // Convert string to string array
      }));
      onFiltersChange(formattedFilters);
    } 
    // Next check initialFilters from props (second priority)
    else if (initialFilters.length > 0) {
      const internalFilters: Filter[] = initialFilters.map(filter => ({
        property: filter.property,
        values: filter.values[0] || ''
      }));
      
      setActiveFilters(internalFilters);
      setPendingFilters(internalFilters);
      
      // Also update filterValues state for display
      initialFilters.forEach(filter => {
        if (filter.values.length > 0) {
          newFilterValues[filter.property] = filter.values[0];
        }
      });
      setFilterValues(newFilterValues);
      console.log('Fetching data with initial filters:', internalFilters);
      
      // Immediately fetch data with initial filters
      fetchData(internalFilters);
      initialFetchMade.current = true;
      
      // No need to notify parent again since these filters came from the parent
    } 
    // If no filters specified, fetch all data (lowest priority)
    else {
      console.log('Fetching data with no filters');
      fetchData([]);
      initialFetchMade.current = true;
    }
    
    // Mark initialization as complete after a short delay to ensure all state updates have processed
    setTimeout(() => {
      isInitializing.current = false;
    }, 100);
  }, []); // Empty dependency array ensures this only runs once on mount

  // Show the first result when data is loaded if showFirstResultInModal is true
  useEffect(() => {
    if (showFirstResultInModal && data.length > 0 && !loading) {
      setSelectedRow(data[0]);
      setShowModal(true);
    }
  }, [data, loading, showFirstResultInModal]);

  // Get only the columns that are being filtered
  const getVisibleColumns = () => {
    const activeProperties = activeFilters.map(f => f.property);
    return finalColumnConfigs.filter(col => 
      activeProperties.includes(col.field) || activeFilters.length === 0
    );
  };

  const handleRowClick = (row: any) => {
    setSelectedRow(row);
    setShowModal(true);
  };

  const LoadingOverlay = () => (
    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
      <div className="flex flex-col items-center space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#db2777] border-t-transparent"></div>
        <p className="text-[#db2777] font-medium">Loading results...</p>
      </div>
    </div>
  );

  // Modal component for detailed view
  const DetailModal = () => {
    if (!selectedRow) return null;

    const formatDetailContent = (content: any, field: string): React.ReactNode => {
      const arrayFields = ['roles', 'reports_to', 'team', 'phrases_topics', 'metrics'];
      const copyFields = ['good_personalized_copy', 'bad_personalized_copy'];
    
      if (Array.isArray(content) && arrayFields.includes(field)) {
        return (
          <div className="flex flex-wrap gap-2">
            {content.map((item, index) => (
              <span
                key={index}
                className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-sm"
              >
                {item}
              </span>
            ))}
          </div>
        );
      }
    
      if (copyFields.includes(field) && typeof content === 'object' && content !== null) {
        const keyOrder = ['copy_reach', 'copy_conversion', 'copy_engagement'];
        const labelMap: Record<string, string> = {
          copy_reach: 'Initial Hook',
          copy_engagement: 'Nurture Copy',
          copy_conversion: 'Call to Action',
        };
      
        const isGoodCopy = field === 'good_personalized_copy';
      
        // Match outer div bg colors for good/bad copy
        const bgColor = isGoodCopy ? 'bg-gray-50' : 'bg-pink-50';
      
        return (
          <div className="space-y-0">
            {keyOrder.map((key) => (
              <div key={key} className="min-h-[200px] flex flex-col justify-start">
                <h4 className="font-semibold text-sm text-pink-700 mb-0.5">{labelMap[key]}</h4>
                <p
                  className={`text-base text-gray-600 whitespace-pre-wrap rounded-md p-3 h-full min-h-[120px] ${bgColor}`}
                >
                  {String(content[key] || '')}
                </p>
              </div>
            ))}
          </div>
        );
      }
      
      return String(content || '');
    };

    // Special render function for fields with ICP variants
    const renderIcpFields = (baseField: string, highIcpField: string) => {
      const baseConfig = fullColumnConfigs.find(c => c.field === baseField);
      const highIcpConfig = fullColumnConfigs.find(c => c.field === highIcpField);
      
      if (!baseConfig) return null;
      
      const baseContent = selectedRow[baseField];
      const highIcpContent = selectedRow[highIcpField];
      
      // If neither field has content, don't render anything
      if (!baseContent && !highIcpContent) return null;
      
      return (
        <div key={baseField} className="col-span-2 mb-6">
          <h4 className="font-medium text-gray-700 text-lg mb-3">{baseConfig.header}</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">ICP highly matches</div>
              <div className="text-gray-600 whitespace-pre-wrap text-base bg-gray-50 p-3 rounded-md h-full">
                {highIcpContent ? formatDetailContent(highIcpContent, highIcpField) : "No data available"}
              </div>
            </div>
            
            <div>
              <div className="text-sm font-medium text-gray-500 mb-1">ICP does not match</div>
              <div className="text-gray-600 whitespace-pre-wrap text-base bg-pink-50 p-3 rounded-md h-full">
                {baseContent ? formatDetailContent(baseContent, baseField) : "No data available"}
              </div>
            </div>
          </div>
        </div>
      );
    };

    const sections: SectionConfig[] = [
      {
        title: 'Copy Strategy',
        fields: ['good_personalized_copy', 'bad_personalized_copy']
      },
      {
        title: 'Role Information',
        fields: ['role_set_name', 'roles', 'reports_to', 'team']
      },
      {
        title: 'Demographics',
        fields: ['gender_distribution', 'age_distribution', 'yoe_distribution', 'salary_range']
      },
      {
        title: 'Detailed Description',
        fields: [],
        customRenders: [
          () => renderIcpFields('what_are_they', 'high_icp_what_are_they'),
          () => renderIcpFields('how_they_contribute', 'high_icp_how_they_contribute')
        ],
        afterCustomRenderFields: ['phrases_topics', 'metrics']
      },
      
      {
        title: 'A Day in the life of this persona',
        fields: ['day_in_the_life_data']
      }
    ];

    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-[95%] h-[90vh] flex flex-col modal-content border-2 border-gray-200 shadow-xl">
          <div className="flex flex-col p-8 pb-4 bg-white border-b sticky top-0 z-20"> {/* Reduced bottom padding */}
            <div className="flex justify-between items-center w-full">
              <div className="flex-1" /> {/* Spacer */}
              <div className="flex gap-4 justify-center flex-1 whitespace-nowrap">
                <span className="text-gray-600">{selectedRow.industry}</span>
                <span className="text-gray-600">•</span>
                <span className="text-gray-600">{selectedRow.employee_range}</span>
                <span className="text-gray-600">•</span>
                <span className="text-gray-600">{selectedRow.role_set_name}</span>
              </div>
            
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700 p-2"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-center mt-2">Persona Details</h2>
          </div>
          
          <div className="overflow-y-auto p-8 flex-1">
            <div className="space-y-8">
              {sections.map((section) => (
                <div key={section.title} className="border-b border-gray-200 pb-6">
                  <h3 className="text-xl font-semibold text-[#db2777] mb-4">{section.title}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Render custom components first if available */}
                    {section.customRenders && section.customRenders.map((render, idx) => (
                      <React.Fragment key={`custom-${idx}`}>
                        {render()}
                      </React.Fragment>
                    ))}
                    
                    {/* Render standard fields */}
                    {section.fields.map((field) => {
                      const config = fullColumnConfigs.find(c => c.field === field);
                      if (!config) return null;
                      const isFullWidth = field === 'day_in_the_life_data';
                      return (
                        <div
                          key={field}
                          className={`space-y-2 ${isFullWidth ? 'col-span-2' : ''}`}
                        >
                          <h4 className="font-medium text-gray-700 text-lg">{config.header}</h4>
                          <div className="text-gray-600 whitespace-pre-wrap text-base">
                            {formatDetailContent(selectedRow[field], field)}
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Render fields that should appear after custom renders */}
                    {section.afterCustomRenderFields && section.afterCustomRenderFields.map((field) => {
                      const config = fullColumnConfigs.find(c => c.field === field);
                      if (!config) return null;
                      return (
                        <div key={field} className="space-y-2">
                          <h4 className="font-medium text-gray-700 text-lg">{config.header}</h4>
                          <div className="text-gray-600 whitespace-pre-wrap text-base">
                            {formatDetailContent(selectedRow[field], field)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const fetchData = async (filters: Filter[]) => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrlMultivac}/targetaudience/search_persona`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          search_fields: filters
        }),
      });
      if (!response.ok) {
        setData([]);
        return;
      }
      const result = await response.json();
      setData(result);
      
      // If we have a preselected persona or need to show the first result and no specific row is selected yet
      if ((preselectedPersona || showFirstResultInModal) && result.length > 0 && !selectedRow) {
        setSelectedRow(result[0]);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setData([]);
    }
    setLoading(false);
    setApplyingFilters(false);
  };

  useEffect(() => {
    // Skip the effect during the initial render
    if (!initialFetchMade.current || isInitializing.current) return;
    
    // Skip if this is still part of initialization
    // This prevents duplicate API calls during initialization
    const isInitialActiveFiltersChange = 
      document.readyState !== 'complete' || 
      performance.now() < 1000; // Skip if within first second of load
    
    if (isInitialActiveFiltersChange) return;
    
    fetchData(activeFilters);
    
    // Convert activeFilters to the format expected by onFiltersChange
    const formattedFilters = activeFilters.map(filter => ({
      property: filter.property,
      values: [filter.values] // Convert string to string array
    }));
    
    onFiltersChange(formattedFilters);
  }, [activeFilters]);

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col space-y-4 bg-white p-4 rounded-lg shadow">
        <div className="flex flex-wrap gap-4">
          {filterOptions.map((option) => (
            <div key={option.property} className="relative filter-dropdown">
              <button
                onClick={() => toggleDropdown(option.property)}
                className={`w-[200px] px-4 py-2 text-left bg-white border border-gray-200 rounded-md ${
                  dropdownStates[option.property] 
                    ? 'focus:outline-none focus:ring-2 focus:ring-[#db2777] focus:border-transparent' 
                    : ''
                }`}
              >
                {filterValues[option.property] || option.label}
              </button>
              {dropdownStates[option.property] && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  <button
                    className={`w-full px-4 py-2 text-left hover:bg-gray-100 ${
                      !filterValues[option.property] ? 'bg-pink-50 text-[#db2777]' : ''
                    }`}
                    onClick={() => handleFilterChange(option.property, '')}
                  >
                    Any
                  </button>
                  {option.values.map((value) => (
                    <button
                      key={value}
                      className={`w-full px-4 py-2 text-left hover:bg-gray-100 ${
                        filterValues[option.property] === value ? 'bg-pink-50 text-[#db2777]' : ''
                      }`}
                      onClick={() => handleFilterChange(option.property, value)}
                    >
                      {value}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          <button 
            onClick={clearAllFilters}
            disabled={pendingFilters.length === 0}
            className="flex items-center px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg
              className="h-4 w-4 mr-2"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            Clear Filters
          </button>

          <button 
            onClick={applyFilters}
            disabled={pendingFilters === activeFilters}
            className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {applyingFilters ? (
              <svg 
                className="animate-spin h-4 w-4 mr-2" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24"
              >
                <circle 
                  className="opacity-25" 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  strokeWidth="4"
                />
                <path 
                  className="opacity-75" 
                  fill="currentColor" 
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            ) : (
              <svg
                className="h-4 w-4 mr-2"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
            {applyingFilters ? 'Applying...' : 'Apply Filters'}
          </button>
        </div>

        {/* Modified Active Filters Display */}
        {pendingFilters.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {pendingFilters.map((filter, index) => (
              <div
                key={index}
                className="flex items-center space-x-1 bg-[#db2777] text-white px-3 py-1 rounded-full"
              >
                <span>{filter.values}</span>
                <button
                  onClick={() => handleFilterChange(filter.property, '')}
                  className="hover:text-gray-200 ml-2"
                >
                  <svg
                    className="h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modified Table with conditional minimum height */}
      <div className={`bg-white rounded-lg shadow relative ${loading ? 'min-h-[400px]' : ''}`}>
        {loading && <LoadingOverlay />}
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full table-fixed border-separate" style={{ borderSpacing: 0 }}>
              <thead className="bg-[#db2777]">
                <tr>
                  {tableColumnConfigs.map((column) => (
                    <th
                      key={column.field}
                      className={`${column.width} sticky top-0 px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider`}
                    >
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={tableColumnConfigs.length} className="px-6 py-4 text-center text-sm text-gray-500" />
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={tableColumnConfigs.length} className="px-6 py-4 text-center text-base text-gray-500 mt-8">
                    Oops! No data to show.
                    </td>
                  </tr>
                ) : (
                  data.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="hover:bg-pink-50 transition-colors cursor-pointer"
                      onClick={() => handleRowClick(row)}
                    >
                      {tableColumnConfigs.map((column) => (
                        <td
                          key={column.field}
                          className={`${column.width} px-6 py-4 whitespace-normal break-words text-sm text-gray-500`}
                        >
                          {formatCellContent(row[column.field], column.field)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* Modal */}
      {showModal && <DetailModal />}
    </div>
  );
};

export default FilteredTable;